import { memo, useCallback, useState } from 'react';

import { Paper } from '@mui/material';

import { ConfirmationDialog } from '@/features/Dashboard/pages/Edit/components';
import type { PastExportDetails } from '@/features/DataHub/api/pastExports.api';
import TokenLink from '@/features/DataHub/modules/Export/components/Shared/TokenLink';
import { ExportItemActions } from '@/features/DataHub/pages/PastExports/components/ExportItem/ExportItemActions';
import { ExportItemDetails } from '@/features/DataHub/pages/PastExports/components/ExportItem/ExportItemDetails';
import { ExportItemHeader } from '@/features/DataHub/pages/PastExports/components/ExportItem/ExportItemHeader';
import type { ExportItemType } from '@/features/DataHub/types/pastExports';

export type DerivedExport = {
  item: ExportItemType;
  details: PastExportDetails;
  isSourceBackedDataItem: boolean;
  resolvedSourceSettings: PastExportDetails['sourceSettings'] | ExportItemType['sourceSettings'];
  optionLabel: string;
  isMissingToken: boolean;
  canDelete: boolean;
  canRegenerate: boolean;
};

type Props = {
  derived: DerivedExport;
  isDeleting: boolean;
  expanded: boolean;
  onToggleExpanded: (id: string, expanded: boolean) => void;
  onDelete: (derived: DerivedExport) => void;
  onRegenerate: (derived: DerivedExport) => void;
};

export const ExportItem = memo(function ExportItem({
  derived,
  isDeleting,
  expanded,
  onToggleExpanded,
  onDelete,
  onRegenerate
}: Props) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const { item, details, optionLabel, isMissingToken, canDelete, canRegenerate } = derived;

  const handleExpandedChange = useCallback(
    (value: boolean) => {
      onToggleExpanded(item.id, value);
    },
    [item.id, onToggleExpanded]
  );

  return (
    <Paper
      sx={{
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 2
      }}
    >
      <ExportItemHeader
        name={item.name}
        status={item.exportStatus}
      />
      <ExportItemDetails
        data={details}
        expanded={expanded}
        isLoading={false}
        optionLabel={optionLabel}
        onExpandedChange={handleExpandedChange}
      />
      {isMissingToken && <TokenLink bgcolor='background.additionalPaper' />}
      <ExportItemActions
        canDelete={canDelete}
        canRegenerate={canRegenerate}
        isDeleting={isDeleting}
        onDelete={() => {
          setConfirmOpen(true);
        }}
        onRegenerate={() => {
          onRegenerate(derived);
        }}
      />
      {confirmOpen && (
        <ConfirmationDialog
          cancelText='Cancel'
          confirmText='Delete'
          open
          text='This export will be permanently deleted.'
          title='Delete past export?'
          onCancel={() => {
            setConfirmOpen(false);
          }}
          onConfirm={() => {
            setConfirmOpen(false);
            onDelete(derived);
          }}
        />
      )}
    </Paper>
  );
});
